<template>
    <card-wrapper :title="Lang.get('employerdashboard::pages/employee/create.title')"
                  :subtitle="Lang.get('employerdashboard::pages/employee/create.subtitle')">

        <template v-slot:nav aria-label="breadcrumb">
            <ol class="breadcrumb font-poppins font-weight-light font-size-14 color-blue px-0 bg-transparent">
                <li class="breadcrumb-item">
                    <a :href="route('nl.employerdashboard.employees.overview', $page.props.auth.user.selected_company.uuid)"
                       class="font-poppins font-weight-semibold color-blue">{{
                            Lang.get('employerdashboard::pages/employee/create.breadcrumb.employee_overview')
                        }}</a>
                </li>
                <li class="breadcrumb-item color-blue" aria-current="page">
                    {{ Lang.get('employerdashboard::pages/employee/create.breadcrumb.new_employee') }}
                </li>
            </ol>
        </template>

        <card-layout :size="'large'">
            <form @submit.prevent="submit" class="p-3">

                <div class="form-row">
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.first_name"
                            :label="Lang.get('employerdashboard::shared.firstname')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.firstname')"
                            :error="errors.first_name"
                            :isValidated="isValidated"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.middle_name"
                            :label="Lang.get('employerdashboard::shared.middlename')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.middlename')"
                            :error="errors.middle_name"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.last_name"
                            :label="Lang.get('employerdashboard::shared.lastname')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.lastname')"
                            :error="errors.last_name"
                            :isValidated="isValidated"
                        />
                    </div>
                </div>

                <div class="form-row mt-3">
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.initials"
                            :label="Lang.get('employerdashboard::shared.initials')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.initials')"
                            :error="errors.initials"
                            :isValidated="isValidated"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4 col-lg-4">
                        <text-input
                            type="date"
                            v-model="form.date_of_birth"
                            :label="Lang.get('employerdashboard::shared.date_of_birth')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.date_of_birth')"
                            :error="errors.date_of_birth"
                            :isValidated="isValidated"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4 col-lg-4">
                        <text-input
                            v-model="form.birthplace"
                            :label="Lang.get('employerdashboard::shared.birth_place')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.birth_place')"
                            :error="errors.birthplace"
                        />
                    </div>
                </div>

                <div class="form-row mt-3 border-bottom"></div>

                <div class="form-row mt-3">
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.street"
                            :label="Lang.get('employerdashboard::shared.street')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.street')"
                            :error="errors.street"
                            :isValidated="isValidated"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.house_number"
                            :label="Lang.get('employerdashboard::shared.house_number')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.house_number')"
                            :error="errors.house_number"
                            :isValidated="isValidated"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.house_number_addition"
                            :label="Lang.get('employerdashboard::shared.house_number_addition')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.house_number_addition')"
                            :error="errors.house_number_addition"
                        />
                    </div>
                </div>

                <div class="form-row mt-3">
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.zip_code"
                            :label="Lang.get('employerdashboard::shared.zipcode')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.zipcode')"
                            :error="errors.zip_code"
                            :isValidated="isValidated"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.city"
                            :label="Lang.get('employerdashboard::shared.city')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.city')"
                            :error="errors.city"
                            :isValidated="isValidated"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <select-input
                            v-model="form.country_code"
                            :label="Lang.get('employerdashboard::shared.country')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.country')"
                            :error="errors.country_code"
                            :isValidated="isValidated"
                        >
                            <option :value="null">{{ Lang.get('employerdashboard::shared.choose') }}</option>
                            <option value="NL" v-if="!form.country">{{ preferred_country }}</option>
                            <option v-for="(countryName, countryCode) in countries" :value="countryCode">
                                {{ countryName }}
                            </option>
                        </select-input>
                    </div>
                </div>

                <div class="form-row mt-3 border-bottom"></div>

                <div class="form-row mt-3">
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.phonenumber"
                            :label="Lang.get('employerdashboard::shared.phone')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.phone')"
                            :error="errors.phonenumber"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.email_personal"
                            :label="Lang.get('employerdashboard::shared.email_personal')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.email_personal')"
                            :error="errors.email_personal"
                            :isValidated="isValidated"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.email_business"
                            :label="Lang.get('employerdashboard::shared.email_business')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.email_business')"
                            :error="errors.email_business"
                            :isValidated="isValidated"
                        />
                    </div>
                </div>

                <div class="form-row mt-3 border-bottom"></div>

                <div class="form-row mt-3">
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.employee_number"
                            :label="Lang.get('employerdashboard::shared.employee_number')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.employee_number')"
                            :error="errors.employee_number"
                            :isValidated="isValidated"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.function"
                            :label="Lang.get('employerdashboard::shared.function')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.function')"
                            :error="errors.function"
                            :isValidated="isValidated"
                        />
                    </div>
                </div>

                <div class="form-row mt-3">
                    <div class="form-group col-md-6 col-lg-4">
                        <text-input
                            type="date"
                            v-model="form.hired_at"
                            :label="Lang.get('employerdashboard::shared.hired_at')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.hired_at')"
                            :error="errors.hired_at"
                            :isValidated="isValidated"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            type="date"
                            v-model="form.fired_at"
                            :label="Lang.get('employerdashboard::shared.fired_at')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.fired_at')"
                            :error="errors.fired_at"
                        />
                    </div>
                </div>

                <div class="form-row mt-3">
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            type="number"
                            v-model="form.hours"
                            :label="Lang.get('employerdashboard::shared.hours')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.hours')"
                            :error="errors.hours"
                            :isValidated="isValidated"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <select-input
                            v-model="form.payroll_tax_credit"
                            :label="Lang.get('employerdashboard::shared.payroll_tax_credit')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.payroll_tax_credit')"
                            :error="errors.payroll_tax_credit"
                            :isValidated="isValidated"
                        >
                            <option :value="null">{{ Lang.get('employerdashboard::shared.choose') }}</option>
                            <option
                                v-for="(payrollTaxCredit, payrollTaxCreditKey) in payroll_tax_credit_options"
                                :value="payrollTaxCreditKey">{{ payrollTaxCredit }}
                            </option>
                        </select-input>
                    </div>
                </div>

                <div class="form-row mt-3" v-if="$page.props.flash.error && !$page.props.flash.error.iobRight">
                    <div class="alert alert-danger">
                        <p>{{ Lang.get('employerdashboard::shared.iob_subtitle') }}</p>
                        <ul v-if="$page.props.flash.error.reasons">
                            <li v-for="reason in $page.props.flash.error.reasons"> {{ reason }}</li>
                        </ul>
                        <template v-if="!$page.props.flash.error.reasons">
                            {{ Lang.get('employerdashboard::pages/employee/create.instructions.title') }}
                        </template>
                    </div>
                </div>

                <div class="form-row mt-3">
                    <div class="form-group col-12 col-md-4 col-lg-3">
                        <button
                            class="btn btn-primary font-poppins font-weight-bold btn-orange border-radius-19 px-4">
                            {{ Lang.get('employerdashboard::shared.save') }}
                        </button>
                    </div>
                    <div class="form-group col-12 col-md-8 col-lg-9">
                        <button
                            class="btn btn-primary font-poppins font-weight-bold btn-orange border-radius-19 px-4">
                            {{ Lang.get('employerdashboard::shared.save_and_continue') }}
                        </button>
                    </div>
                </div>
            </form>
        </card-layout>
        <div class="col-12 col-md-4 mb-4" v-if="showInstructions">
            <div class="row">
                <div class="col-12 mb-4">
                    <div class="card h-100 border-radius-8 shadow">
                        <div class="card-body">
                            <h4 class="card-title font-poppins font-weight-extrabold color-blue font-size-14">
                                {{ Lang.get('employerdashboard::pages/employee/create.instructions.title') }}
                            </h4>
                            <div class="card-text color-dark-blue font-size-14">
                                {{ Lang.get('employerdashboard::pages/employee/create.instructions.description') }}<br/>
                                <br/>
                                <strong class="font-aileron-bold">
                                    {{ Lang.get('employerdashboard::pages/employee/create.instructions.step_1') }}:
                                </strong>
                                {{
                                    Lang.get('employerdashboard::pages/employee/create.instructions.step_1_description')
                                }}<br/>
                                <br/>
                                <strong class="font-aileron-bold">
                                    {{ Lang.get('employerdashboard::pages/employee/create.instructions.step_1') }}:
                                </strong>
                                {{
                                    Lang.get('employerdashboard::pages/employee/create.instructions.step_2_description')
                                }}<br/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </card-wrapper>
</template>

<script>
import CardWrapper from '@/Shared/CardWrapper';
import CardLayout from "@/Shared/CardLayout";
import TextInput from "@/Shared/TextInput";
import SelectInput from "@/Shared/SelectInput";
import FormError from "@/Shared/FormError";

export default {
    metaInfo() {
        return {
            title: `${this.Lang.get('employerdashboard::pages/employee/create.title')}`,
        }
    },
    components: {
        CardLayout,
        CardWrapper,
        FormError,
        SelectInput,
        TextInput,
    },
    props: {
        errors: Object,
        employee: Object,
        payroll_tax_credit_options: Object,
        countries: Object,
        preferred_country: String,
        isValidated: {
            type: Boolean,
            default() {
                return false;
            },
        },
    },

    data() {
        return {
            showInstructions: false,
            contract: false,
            form: {
                initials: null,
                date_of_birth: null,
                birthplace: null,
                employee_number: null,
                first_name: null,
                middle_name: null,
                last_name: null,
                email: null,
                email_personal: null,
                email_business: null,
                street: null,
                house_number: null,
                house_number_addition: null,
                city: null,
                zip_code: null,
                country_code: null,
                phonenumber: null,
                hours: null,
                function: null,
                payroll_tax_credit: null,
                hired_at: null,
                fired_at: null,
            },
        }
    },

    methods: {
        submit() {
            this.$inertia.post(this.route('nl.employerdashboard.employee.store.attempt', {'company_as_uuid': this.$page.props.auth.user.selected_company.uuid}), this.form)
        },
    },
}
</script>
